import React from 'react';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AboutPage.module.css';

const WhatYouCanDo = () => (
  <section className={css.activitySection} aria-labelledby="what-you-can-do">
    <h2 id="what-you-can-do">What you can do on PageHop</h2>
    <div className={css.activityGrid}>
      <section>
        <h3>Buy</h3>
        <p>
          Browse books and bookish goods from Canadian sellers, view detailed listings, explore
          Seller Shops, and purchase through PageHop.
        </p>
      </section>
      <section>
        <h3>Sell</h3>
        <p>
          Create listings, manage what you have for sale, and build a Shop that brings all of your
          listings together.
        </p>
      </section>
      <section>
        <h3>Find</h3>
        <p>Use In Search Of when you know exactly what you want but cannot find it listed yet.</p>
      </section>
      <section>
        <h3>Save</h3>
        <p>
          Keep track of books and listings you want to come back to through My Shelf and wishlist
          features where available.
        </p>
      </section>
      <section>
        <h3>Discover</h3>
        <p>
          Explore what is newly listed, browse Seller Shops, and find books you may not have known
          you were looking for.
        </p>
      </section>
    </div>
  </section>
);

const AboutPage = () => (
  <Page
    title="About PageHop Books | PageHop Books"
    description="PageHop Books is a Canadian marketplace for buying and selling books and bookish goods."
  >
    <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
      <main className={css.root}>
        <div className={css.content}>
          <header className={css.pageHeader}>
            <p className={css.eyebrow}>ABOUT PAGEHOP BOOKS</p>
            <h1>About PageHop Books</h1>
            <p className={css.lede}>
              PageHop Books is a Canadian marketplace for books and bookish goods, with a place for
              the editions readers care about most.
            </p>
          </header>

          <section className={css.section}>
            <h2>A Canadian marketplace built for book people</h2>
            <p>
              PageHop Books is a Canadian marketplace for buying and selling books, special
              editions, signed copies, rare finds, and bookish goods.
            </p>
            <p>
              It is designed to make it easier to discover great books, sell what you are ready to
              move on from, and buy from other readers and sellers across Canada.
            </p>
            <p>
              No endless scrolling through unrelated listings. No trying to explain what a FairyLoot
              edition means to a general marketplace. Just a book-focused place to browse, buy,
              sell, and connect.
            </p>
          </section>

          <section className={css.section}>
            <h2>More than a listing</h2>
            <p>
              Every seller on PageHop has their own Shop. Buyers can browse everything a seller has
              available in one place, see Shop information, reviews, specialties, and bundle offers
              where available.
            </p>
            <p>
              For sellers, listings do not live as isolated posts. They become part of a storefront
              buyers can come back to.
            </p>
          </section>

          <section className={css.section}>
            <h2>Built for the way book people actually buy and sell</h2>
            <p>
              PageHop is being built around the things that matter when you are buying books online:
            </p>
            <ul className={css.featureList}>
              <li>Clear condition details</li>
              <li>Honest flaw disclosure</li>
              <li>Good photos</li>
              <li>Edition information when it matters</li>
              <li>Seller reviews</li>
              <li>Secure marketplace transactions</li>
              <li>Easy access to a seller's full Shop</li>
              <li>In Search Of posts for books you cannot find</li>
              <li>Tools that make browsing and selling easier</li>
            </ul>
            <p>
              Special editions are still a big part of PageHop, but they are one category within a
              broader book marketplace.
            </p>
            <p>
              A regular hardcover, an out-of-print set, a signed copy, a sprayed-edge edition, or a
              stack of books you are ready to pass along can all have a place here.
            </p>
          </section>

          <section className={css.section}>
            <h2>Canadian by design</h2>
            <p>
              PageHop is focused on buyers and sellers in Canada. The goal is to make domestic book
              buying and selling simpler, with fewer cross-border surprises and a marketplace built
              around Canadian users from the start.
            </p>
            <p>
              That means less friction around things like currency, shipping, and buying from
              sellers who are actually in Canada.
            </p>
          </section>

          <WhatYouCanDo />

          <section className={css.section}>
            <h2>Why PageHop exists</h2>
            <p>
              Buying and selling books in Canada should not require piecing together Facebook
              groups, general marketplaces, spreadsheets, DMs, and cross-border workarounds.
            </p>
            <p>
              PageHop is being built to give book buyers and sellers one place that understands what
              they are actually listing and looking for. A marketplace where the books are the
              point.
            </p>
          </section>

          <section className={css.ctaSection} aria-labelledby="about-cta-title">
            <div>
              <h2 id="about-cta-title">Ready to find your next shelf favourite?</h2>
              <p>Browse the marketplace or start listing the books you are ready to sell.</p>
            </div>
            <div className={css.ctaActions}>
              <NamedLink className={css.secondaryAction} name="SearchPage">
                Browse Books
              </NamedLink>
              <NamedLink className={css.primaryAction} name="NewListingPage">
                Start Selling
              </NamedLink>
            </div>
          </section>
        </div>
      </main>
    </LayoutSingleColumn>
  </Page>
);

export default AboutPage;
export { WhatYouCanDo };